<template>
    <div
        class="WCheckbox"
        :class="{ checked: isChecked, error, disabled }"
        @click="emitChange"
    >
        <div class="Icon">
            <WIcon
                v-if="isChecked"
                :color="iconColor"
                :size="16"
                :icon="icon"
            />
        </div>
        <input
            :id="id"
            ref="checkbox"
            :value="value"
            :checked="isChecked"
            :name="name"
            :disabled="disabled"
            class="Input"
            type="checkbox"
            v-on="inputListeners"
        >
        <label
            :for="value"
            class="Label"
        >
            {{ label }}
        </label>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { merge, filter, includes } from 'lodash'
import { generateIdMixin } from '@/mixins'

export default {
    name: 'WCheckbox',
    mixins: [generateIdMixin],
    model: {
        prop: 'modelValue',
        event: 'input',
    },
    props: {
        error: VueTypes.bool.def(false),
        label: VueTypes.string.isRequired,
        modelValue: VueTypes.oneOfType([Boolean, Array, Number, Object]).isRequired,
        name: VueTypes.string.isRequired,
        icon: VueTypes.string.def('close'),
        disabled: VueTypes.bool.def(false),
        value: VueTypes.oneOfType([Boolean, String, Number, Object]).isRequired,
    },
    data () {
        return {
            checkedProxy: this.containsValue(), // True or false, we use this for check state
        }
    },
    computed: {
        isChecked () {
            if (this.isSingleCheckbox) return this.modelValue
            const isValueInCheckedProxy = includes(this.checkedProxy, this.value)
            return this.checkedProxy || isValueInCheckedProxy
        },
        isSingleCheckbox () {
            return typeof this.modelValue === 'boolean'
        },
        inputListeners () {
            return merge({}, this.$listeners, {
                input: (event) => {
                    this.emitChange()
                },
            })
        },
        iconColor () {
            if (this.error) return 'red'
            if (this.disabled) return 'grayMedium'
            return 'primary'
        },
    },
    watch: {
        modelValue (val) {
            if (this.isSingleCheckbox) {
                this.checkedProxy = val
            } else {
                this.checkedProxy = this.containsValue()
            }
        },
    },
    methods: {
        emitChange () {
            if (!this.disabled) {
                if (this.isSingleCheckbox) {
                    this.emitBooleanValue()
                } else {
                    this.emitArrayValue()
                }
            }
        },
        emitBooleanValue () {
            this.checkedProxy = !this.checkedProxy
            return this.$emit('input', this.checkedProxy)
        },
        containsValue () {
            return includes(this.modelValue, this.value)
        },
        emitArrayValue () {
            let newValues = []
            if (this.checkedProxy) {
                this.checkedProxy = false
                newValues = filter(this.modelValue, val => val !== this.value)
            } else {
                this.checkedProxy = true
                newValues = [...this.modelValue, this.value]
            }
            return this.$emit('input', newValues)
        },
    },
}
</script>

<style scoped lang="scss">
.WCheckbox {
	display: flex;
	margin-bottom: 15px;
	.Input {
		display: none;
	}
	.Icon {
		position: relative;
		display: inline-flex;
		justify-content: center;
		align-items: center;
		width: 16px;
		height: 16px;
		border: 2px solid $gray-02;
		background-color: transparent;
		touch-action: manipulation;
		&:hover {
			cursor: pointer;
		}
	}

	.Label {
		padding-left: 5px;
		margin-bottom: 0;
		color: $form-color;
		font-weight: $form-font-weight;
		font-size: $form-font-size;
		&:hover {
			cursor: pointer;
		}
	}
	&.error {
		.Icon {
			border: 2px solid $danger;
		}
		.Label {
			color: $danger;
		}
	}
	&.disabled {
		.Label {
			opacity: 0.5;
			cursor: not-allowed;
		}
		.Icon {
			border-color: $gray-medium;
			background-color: $gray-light;
			cursor: not-allowed;
		}
	}

}

</style>
